import { AfterViewInit, Component, OnDestroy, TemplateRef, ViewChild, ChangeDetectionStrategy, inject } from '@angular/core';
import { NavigationEnd, Router, RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router';
import { asyncScheduler, filter, interval, observeOn, Subscription } from 'rxjs';
import { EvButtonToggle, EvButtonToggleComponent } from './lib/components/ev-button-toggle.component';
import { URL_YT_NEXTCOMUNICA } from './lib/constants';
import { ApplicationService } from './lib/services/application.service';
import { DialogMaterialService } from './lib/services/dialog-material.service';
import { DisplayService } from './lib/services/display.service';
import { DocumentsService } from './lib/services/documents.service';
import { FunzionalitaService } from './lib/services/funzionalita.service';
import { ToolbarAction, ToolbarService } from './lib/services/toolbar.service';
import { UtenteService } from './lib/services/utente.service';
import { SwUpdateService } from './lib/sw/sw-update.service';
import { LogService } from './lib/services/log.service';
import { MatToolbar } from '@angular/material/toolbar';
import { MatIconButton, MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { EvLogoComponent } from './lib/components/ev-logo.component';
import { MatSidenavContainer, MatSidenav, MatSidenavContent } from '@angular/material/sidenav';
import { MatNavList, MatDivider, MatListItem } from '@angular/material/list';
import { EvUserProfileComponent } from './lib/components/ev-user-profile/ev-user-profile.component';
import { NewsAccessoComponent } from './portale/pages/news/news-accesso.component';
import { AsyncPipe } from '@angular/common';
import { MatBadge } from '@angular/material/badge';
import { ComunicazioniService } from './applicazione/services/comunicazioni.service';
import { AvvisiComunicazioniService } from './applicazione/services/avvisi-comunicazioni.service';


@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [MatToolbar, MatIconButton, MatIcon, EvLogoComponent, MatButton, RouterLink, RouterLinkActive, MatSidenavContainer, MatSidenav, MatNavList, EvUserProfileComponent, MatDivider, MatListItem, MatSidenavContent, RouterOutlet, EvButtonToggleComponent, NewsAccessoComponent, AsyncPipe, MatBadge]
})
export class AppComponent implements AfterViewInit, OnDestroy {
  private log = inject(LogService);
  protected router = inject(Router);
  protected swUpdateService = inject(SwUpdateService);
  protected funzionalitaService = inject(FunzionalitaService);
  utenteService = inject(UtenteService);
  applicationService = inject(ApplicationService);
  documentsService = inject(DocumentsService);
  protected toolbarService = inject(ToolbarService);
  private dialogMaterialService = inject(DialogMaterialService);
  displayService = inject(DisplayService);
  protected comunicazioniService = inject(ComunicazioniService);
  protected avvisiComunicazioniService = inject(AvvisiComunicazioniService);
  // I contatori possono essere restituiti subito dalla cache del service worker.
  // Li notifichiamo nel ciclo successivo per non modificare il badge mentre Angular
  // sta ancora verificando la toolbar appena creata.
  protected richiesteNonLette$ = this.comunicazioniService.richiesteNonLette$.pipe(observeOn(asyncScheduler));
  protected avvisiNonLetti$ = this.avvisiComunicazioniService.avvisiNonLetti$.pipe(observeOn(asyncScheduler));

  title = 'salute-con-te-client'

  showAppName: boolean = false
  showAppLogo: boolean = true
  currentYear = new Date().getFullYear()

  URL_YT_NEXTCOMUNICA = URL_YT_NEXTCOMUNICA

  static instance
  loginSub: Subscription
  datiUtentePresentiSub: Subscription
  isPagamentiVisibile = false
  funzionalitaLoadedSub: Subscription
  toolbarActionSub: Subscription
  avvisiSub: Subscription
  richiesteSub: Subscription
  routerSub: Subscription
  contatoriTimerSub: Subscription
  currentToolbarAction: ToolbarAction
  protected showHomeShortcut = false
  private contatoriComunicazioniAbilitati = false

  @ViewChild('swDataRef') infoTemplateRef: TemplateRef<any>
  @ViewChild('snav') sidenav: MatSidenav


  buttons: Partial<EvButtonToggle>[] = [
    {
      value: 'home',
      text: 'Home',
      url: '/homepage',
      icon: 'home'
    },
    {
      value: 'calendario',
      text: 'Agenda',
      url: '/calendario',
      icon: 'event'
    },
    {
      value: 'ricette',
      text: 'Farmaci',
      url: '/terapiaContinuativa',
      icon: 'description'
    },
    {
      value: 'comunicazioni',
      text: 'Messaggi',
      url: '/comunicazioni/comunicazioni',
      icon: 'chat'
    }
  ]

  ngAfterViewInit(): void {
    setTimeout(() => {

      this.updateHomeShortcutVisibility(this.router.url)
      this.routerSub = this.router.events.pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd)
      ).subscribe(event => {
        this.updateHomeShortcutVisibility(event.urlAfterRedirects)
        this.aggiornaContatoriComunicazioni(event.urlAfterRedirects)
      })

      // Aggiorna entrambi i badge anche quando l'utente resta sulla stessa pagina.
      this.contatoriTimerSub = interval(60000).subscribe(() => this.aggiornaContatoriComunicazioni())

      this.funzionalitaLoadedSub = this.funzionalitaService.funzionalitaLoaded$.subscribe(() => {
        this.contatoriComunicazioniAbilitati = true
        this.aggiornaContatoriComunicazioni()

        if (!this.funzionalitaService.isFunzionalitaAbilitata('Appuntamenti')) {
          this.buttons = this.buttons.filter(b => b.icon !== 'event')
        }
      })

      this.datiUtentePresentiSub = this.utenteService.datiUtentePresenti.subscribe(value => {
        if (value) {
          this.isPagamentiVisibile = this.utenteService.isPagamentiVisibile()
        }
      })

      this.toolbarActionSub = this.toolbarService.showAction.subscribe(action => {
        setTimeout(() => {
          this.currentToolbarAction = action
        }, 0)
      })
    }, 0);
  }

  showSwData() {
    this.dialogMaterialService.open(this.infoTemplateRef)
  }

  toggleSidenav() {
    this.sidenav?.toggle()
  }

  toolbarActionClick(action: ToolbarAction) {
    // se ho le comunicazioni disabilitate non lancio l'azione
    if (this.funzionalitaService.isComunicazioniBloccate() && !this.currentToolbarAction.alwaysEnabled) {
      this.log.messageWarning("Attenzione", this.funzionalitaService.getTestoRispostaAutomatica())
      return
    }

    if (action.action) {
      action.action(action, action.data)
    }
  }

  private updateHomeShortcutVisibility(url: string) {
    this.showHomeShortcut = url.startsWith('/comunicazioni/richieste') || url.startsWith('/comunicazioni/avvisi')
  }

  private aggiornaContatoriComunicazioni(url = this.router.url) {
    const isLogoutNavigation = url.startsWith('/logout')
    const hasSession = !!localStorage.getItem('saluteconte.token')

    // L'app root resta attiva anche quando si passa alle rotte pubbliche. Non
    // aggiornare i badge mentre il logout sta rimuovendo la sessione o quando
    // non esiste più un token valido.
    if (!this.contatoriComunicazioniAbilitati || isLogoutNavigation || !hasSession) {
      this.avvisiSub?.unsubscribe()
      this.richiesteSub?.unsubscribe()
      return
    }

    this.avvisiSub?.unsubscribe()
    this.avvisiSub = this.avvisiComunicazioniService.aggiornaNumeroAvvisiNonLetti().subscribe()
    this.richiesteSub?.unsubscribe()
    this.richiesteSub = this.comunicazioniService.aggiornaRichiesteNonLette().subscribe()
  }

  ngOnDestroy(): void {
    this.funzionalitaLoadedSub?.unsubscribe()
    this.loginSub?.unsubscribe()
    this.toolbarActionSub?.unsubscribe()
    this.datiUtentePresentiSub?.unsubscribe()
    this.avvisiSub?.unsubscribe()
    this.richiesteSub?.unsubscribe()
    this.routerSub?.unsubscribe()
    this.contatoriTimerSub?.unsubscribe()
  }
}
